<template>
  <div class="group-item">
    <i v-if="!isConsultant" class="i-util-bars group-item-handle">
    </i>
    <div :title="item.name" class="title">{{ shortVersion(item.name) }}</div>
    <div class="head-count">{{ item.animal_count }} {{ 'base.heads' | translate }}</div>
    <div class="weight">
      <div class="title no-wrap">{{ 'base.weight_per_head' | translate }}</div>
      <div class="value">{{ weightPerHead }} {{ 'base.kg' | translate }}</div>
    </div>
    <div class="weight">
      <div class="title no-wrap">{{ 'base.load_weight' | translate }} ({{ 'base.kg' | translate }}):</div>
      <div class="value">
        <input 
          @input="onPlanWeightChange" 
          @blur="onPlanWeightBlur"
          class="input-number" 
          step="1" 
          type="number" 
          v-model.number="localPlanWeight"
          name="planWeight" 
          id="planWeight" 
        />
      </div>
    </div>
    <ui-number-input 
      ref="coefficientInput"
      :min="1" 
      :max="500" 
      :decimalPlaces="1" 
      :reactive="false"
      postfix="%" 
      v-model="displayCoefficient"
      @click.native.stop 
      @input="onCoefficientBlur"
    />
  </div>
</template>

<script>
import UiNumberInput from '@/components/ui/ui-number-input/UiNumberInput.vue'

export default {
  name: 'GroupItem',

  props: {
    item: Object,
    baseWeightPerAnimal: Number
  },

  components: {
    UiNumberInput
  },

  data () {
    return {
      internalPlanWeight: 0,
      isManualInput: false,
      isMounted: false,
      initialPlanWeight: 0,
      initialCoefficient: 0,
      coefficientInputId: null
    }
  },

  mounted () {
    this.internalPlanWeight = this.item.plan_weight
    this.initialPlanWeight = this.item.plan_weight
    this.initialCoefficient = this.item.coefficient

    this.$nextTick(() => {
      if (this.$refs.coefficientInput) {
        this.coefficientInputId = this.$refs.coefficientInput._uid
      }

      this.isMounted = true
      this.watchCoefficientInput()
    })

    // Listen for focus events from other inputs to auto-close this one
    window.addEventListener('ui-number-input-focus', this.onOtherInputFocus)
    document.addEventListener('mousedown', this.onDocumentClick)
  },

  beforeDestroy () {
    window.removeEventListener('ui-number-input-focus', this.onOtherInputFocus)
    document.removeEventListener('mousedown', this.onDocumentClick)
  },

  methods: {
    onChange () {
      this.$emit('on-change')
    },

    onSaveDirectly () {
      this.$emit('on-save-direct')
    },

    onPlanWeightChange () {
      this.isManualInput = true
      const value = parseFloat(this.internalPlanWeight)

      if (!isNaN(value) && this.baseWeightPerAnimal && this.item.animal_count > 0) {
        this.item.plan_weight = Math.round(value)

        const baseWeight = this.item.animal_count * this.baseWeightPerAnimal
        const calculatedCoefficient = (this.item.plan_weight / baseWeight) * 100

        this.item.coefficient = Math.round(calculatedCoefficient * 10) / 10
      }

      setTimeout(() => {
        this.isManualInput = false
      }, 100)
    },

    onPlanWeightBlur () {
      const changed = Math.abs(this.item.plan_weight - this.initialPlanWeight) > 0.01

      if (changed) {
        this.onSaveDirectly()
        this.initialPlanWeight = this.item.plan_weight
        this.initialCoefficient = this.item.coefficient
      }
    },

    onCoefficientBlur (value) {
      if (this.isManualInput || !this.isMounted) {
        return
      }

      const exactCoefficient = parseFloat(value)
      this.item.coefficient = exactCoefficient

      // Recalculate plan_weight based on coefficient
      if (this.baseWeightPerAnimal && this.item.animal_count > 0) {
        const baseWeight = this.item.animal_count * this.baseWeightPerAnimal
        const calculatedWeight = (exactCoefficient / 100) * baseWeight
        const roundedWeight = Math.round(calculatedWeight)

        this.$set(this.item, 'plan_weight', roundedWeight)
        this.internalPlanWeight = roundedWeight
      }

      const changed = Math.abs(exactCoefficient - this.initialCoefficient) > 0.01

      if (changed) {
        this.onSaveDirectly()
        this.initialCoefficient = exactCoefficient
        this.initialPlanWeight = this.item.plan_weight
      }
    },

    watchCoefficientInput () {
      // Watch currentValue for real-time UI updates without saving
      if (this.$refs.coefficientInput) {
        this.$watch(() => this.$refs.coefficientInput.currentValue, (newValue) => {
          if (this.isManualInput || !this.isMounted) return

          const exactCoefficient = parseFloat(newValue)
          if (isNaN(exactCoefficient)) return

          this.item.coefficient = exactCoefficient

          if (this.baseWeightPerAnimal && this.item.animal_count > 0) {
            const baseWeight = this.item.animal_count * this.baseWeightPerAnimal
            const calculatedWeight = (exactCoefficient / 100) * baseWeight
            const roundedWeight = Math.round(calculatedWeight)

            this.$set(this.item, 'plan_weight', roundedWeight)
            this.internalPlanWeight = roundedWeight
          }
        })
      }
    },

    onOtherInputFocus (event) {
      // When focus moves to another ui-number-input, close this one
      if (event.detail !== this.coefficientInputId) {
        if (this.$refs.coefficientInput && this.$refs.coefficientInput.toggled) {
          this.$refs.coefficientInput.blur()
        }
      }
    },

    onDocumentClick (event) {
      // Close input when clicking outside
      if (this.$refs.coefficientInput && this.$refs.coefficientInput.toggled) {
        const clickedInsideMyInput = this.$refs.coefficientInput.$el.contains(event.target)

        if (!clickedInsideMyInput) {
          this.$refs.coefficientInput.blur()
        }
      }
    },

    shortVersion (str) {
      return str.length > 14 ? (str.substring(0, 14) + '...') : str
    }
  },

  computed: {
    localPlanWeight: {
      get () {
        return Math.round(this.internalPlanWeight)
      },
      set (value) {
        this.internalPlanWeight = value
      }
    },

    displayCoefficient: {
      get () {
        // Round to 1 decimal place for display
        return Math.round(this.item.coefficient * 10) / 10
      },
      set (value) {
        this.item.coefficient = value
      }
    },

    weightPerHead () {
      if (!this.item.animal_count || this.item.animal_count === 0) return '-'
      const value = (this.item.plan_weight / this.item.animal_count).toFixed(2)
      return !isNaN(value) ? value : '-'
    }
  },

  watch: {
    'item.plan_weight': {
      handler (newVal) {
        if (!this.isManualInput) {
          this.internalPlanWeight = newVal
        }
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
.group-item-handle {
  cursor: move;
}

.no-wrap {
  white-space: nowrap;
}

.group-item {
  position: relative;
  padding: 0 15px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #ececec;

  &__remove {
    cursor: pointer;
    color: #fff;
    font-size: 1.5rem;
    text-transform: uppercase;
    height: 4rem;
    width: 100%;
    background-color: #FF6158;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.15);

    &:hover {
      background-color: darken(#FF6158, 3%);
    }
  }
}

.group-item>.title {
  cursor: pointer;
  position: relative;
  font-size: 1.7rem;
  width: 16%;
  margin-right: 1.5rem;
  padding-left: 1rem;
}

.group-item .head-count {
  width: 9%;
  font-size: 17px;
}

.weight {
  width: 23%;
  margin-right: 2rem;
  display: flex;
  align-items: center;
}

.weight .title {
  font-size: 14px;
  font-weight: 300;
  margin-right: 20px;
}

.weight .value {
  font-size: 15px;
}

.percent-input {
  position: absolute;
  right: 15px;
}

.input-number {
  width: auto;
  max-width: 9rem;
  padding: .5rem;
  text-align: right;
  background-color: #F4F4F4;
  font-weight: 700;
  border-bottom: 1px solid #D8D8D8;
}
</style>